import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { debounce } from 'lodash';
import { getLocationFromMap } from './map';
import { setMapLocation } from '../../actions/mapV2';
import useMap from '../../hooks/map/useMap';
import useMapLocation from '../../hooks/map/useMapLocation';
import { locationObjectToString } from '../../utils/urls/mapPaths';

/**
 * @typedef {import ('../../propTypes/map/location').Location} Location
 */

/**
 * @description
 * We use the zoom and center for positioning the map so we want it to stay synced with the redux store
 */
const useSyncMapLocationToStore = () => {
  const dispatch = useDispatch();
  const [map] = useMap();
  const { location: currLocation } = useMapLocation();
  const debouncedCallbackRef = useRef(null);
  const mountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doSyncMapLocation = useCallback(
    /** @param {Location} location */
    (location) => {
      // Only sync if component is still mounted
      if (!mountedRef.current) {
        return;
      }

      const locationChanged =
        locationObjectToString(location) !== locationObjectToString(currLocation);
      if (locationChanged) {
        dispatch(setMapLocation(location));
      }
    },
    [dispatch, currLocation],
  );

  const onMoveEndCallback = useRef(doSyncMapLocation);

  useEffect(() => {
    onMoveEndCallback.current = doSyncMapLocation;
  }, [doSyncMapLocation]);

  useEffect(() => {
    if (map) {
      try {
        const debouncedMoveCallback = debounce(() => {
          try {
            // Check if map still exists, has necessary properties, and component is mounted
            if (mountedRef.current && map && map.getCenter && typeof map.getCenter === 'function') {
              onMoveEndCallback.current(getLocationFromMap(map));
            }
          } catch (err) {
            console.log('[useSyncMapLocationToStore] Error in moveend callback:', err);
          }
        }, 750);

        // Store reference to the debounced callback for cleanup
        debouncedCallbackRef.current = debouncedMoveCallback;

        map.on('moveend', debouncedMoveCallback);

        // Return cleanup function
        return () => {
          // Cancel any pending debounced calls
          if (debouncedCallbackRef.current) {
            debouncedCallbackRef.current.cancel();
          }

          // Remove the event listener if map still exists
          if (map && typeof map.off === 'function') {
            map.off('moveend', debouncedCallbackRef.current);
          }
        };
      } catch (err) {
        console.log('[useSyncMapLocationToStore] Error setting up map listener:', err);
      }
    }

    return undefined;
  }, [map]);
};

export default useSyncMapLocationToStore;
